import { useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";

import { getInitialsFromFullName } from "../constants/constants";
import { Period } from "../features/billing/SelectDate";
import { useAppSelector } from "../redux/redux";
import { useGetPaymentStatusesQuery } from "../services/BillingService";
import { getLinkedFirm } from "../sessionStorage/sessionStorage";
import { useGetSearchCustomersQuery } from "../slices/ContactsSlice";
import { SelectOptionType } from "../types/types";
import { TimePeriod, useDetectTimePeriod } from "./useDetectTimePeriod";

export const usePaymentFiltersController = () => {
  const user = useAppSelector((state) => state.appReducer.user);

  const [searchClients, setSearchClients] = useState("");
  const [filter, setFilter] = useState<Period>({
    period: TimePeriod.All,
    from: null,
    to: null,
  });

  const [filterByContact, setFilterByContact] = useState<SelectOptionType[]>(
    [],
  );
  const [filterByLabel, setFilterByLabel] = useState<SelectOptionType[]>([]);

  const orgId = getLinkedFirm()?.orgId || "";
  const debounceSearchClients = useDebounce(searchClients, 300);

  const { from, to } = useDetectTimePeriod(
    filter.period
      ? {
          period: filter.period,
          from: filter.from,
          to: filter.to,
        }
      : null,
  );

  const { data: contacts, isLoading: isLoadingContacts } =
    useGetSearchCustomersQuery(
      {
        searchQuery: debounceSearchClients || "ab",
        orgId,
        userId: user.id,
        isActive: true,
      },
      {
        skip: !orgId || !user.id,
      },
    );

  const { data: statuses } = useGetPaymentStatusesQuery(orgId, {
    skip: !orgId,
  });

  const labelOptions = useMemo<SelectOptionType[]>(
    () =>
      statuses?.map((item) => ({
        label: item?.name,
        value: item?.id,
        color: item?.color,
      })) || [],
    [statuses],
  );

  const clientOptions = useMemo<SelectOptionType[]>(() => {
    return (
      contacts?.map((contact) => ({
        value: contact?.customerId,
        label: `${contact.customerName}`,
        avatar: getInitialsFromFullName(contact?.customerName),
        avatarColor: contact.businessContactId ? "purple" : "blue",
      })) || []
    );
  }, [contacts]);

  return {
    filter,
    setFilter,

    from,
    to,

    searchClients,
    setSearchClients,

    filterByContact,
    setFilterByContact,

    filterByLabel,
    setFilterByLabel,

    clientOptions,
    labelOptions,

    isLoadingContacts,
  };
};
